import React, { useState } from 'react';
import { Trans } from '@lingui/macro';
import styled from 'styled-components';

import StarRating from '@rover/components/src/formFields/StarRating';
import ArrowLeft from '@rover/icons/svg/arrow-left.svg';
import ArrowRight from '@rover/icons/svg/arrow-right.svg';
import { Box, Button, Flex, Image, Paragraph } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import GroupStars1 from '@rover/react-lib/src/images/homepage/rover-protect/GroupStars1.png';
import Quote from '@rover/react-lib/src/images/homepage/rover-protect/Quote.png';
import Unquote from '@rover/react-lib/src/images/homepage/rover-protect/Unquote.png';
import { getSourceDescription } from '@rover/rsdk/src/modules/Network/userAgent';

import CarouselPageIndicator from './CarouselPageIndicator';

export type CarouselData = {
  src: string;
  description: string;
  comment?: string;
};

export type Props = {
  carouselData: CarouselData[];
};

export const StyledButton = ({ ...props }): JSX.Element => {
  const { textColor } = useTheme();
  return (
    <Button
      aria-label="icon only button"
      m="2x"
      px="4x"
      py="3x"
      variant="flat"
      sx={{ color: textColor.primary, boxShadow: '0px 4px 16px rgba(9, 1, 1, 0.2)' }}
      {...props}
    />
  );
};

const Carousel = ({ carouselData }: Props): JSX.Element => {
  const { radii } = useTheme();
  const platform = getSourceDescription();
  const [activeIndex, setActiveIndex] = useState(0);

  const nextSlide = (): void => {
    const newIndex = activeIndex >= carouselData.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(newIndex);
  };

  const prevSlide = (): void => {
    const newIndex = activeIndex <= 0 ? carouselData.length - 1 : activeIndex - 1;
    setActiveIndex(newIndex);
  };
  const StyledImage = styled(Image)`
    border-radius: ${({ theme }) => theme.radii.primary};
  `;

  return (
    <>
      {platform === 'web' && (
        <Flex flexDirection="column">
          <Box position="relative" left="-2%" bottom="-60px">
            <Image description="stars" height="auto" width="150px" src={GroupStars1} />
          </Box>
          <Flex alignItems="center">
            <StyledButton icon={ArrowLeft} onClick={prevSlide} />
            <Flex
              flexDirection={['column', 'column', 'row']}
              alignItems="center"
              background="primary"
              sx={{ boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)' }}
              p="5x"
              borderRadius={radii.primary}
              mt="0x"
              mb="0x"
              mr="8x"
              ml="24x"
            >
              <Flex
                position="relative"
                left={['0px', '0px ', '-90px']}
                bottom={['0px', '0px ', '60px']}
              >
                <StyledImage
                  description={carouselData[activeIndex].description}
                  height="auto"
                  width="auto"
                  src={carouselData[activeIndex].src}
                />
              </Flex>
              <Box flex="1">
                <Image
                  description="quote"
                  height="auto"
                  width="auto"
                  src={Quote}
                  mt={['0x', '4x', '0x']}
                />
                <Box paddingX="8x" pt="2x">
                  <Paragraph>{carouselData[activeIndex].comment}</Paragraph>
                  <Flex flexDirection="row" alignItems="center" flex="1" mt="4x">
                    <Box mr="1x">
                      <Trans>5.0</Trans>
                    </Box>
                    <Box>
                      <StarRating rating={5} />
                    </Box>
                  </Flex>
                </Box>
                <Flex justifyContent="flex-end" pr="8x">
                  <Image description="unquote" height="auto" width="auto" src={Unquote} />
                </Flex>
              </Box>
            </Flex>
            <StyledButton icon={ArrowRight} onClick={nextSlide} />
          </Flex>
          <Flex position="relative" mt="16x">
            <CarouselPageIndicator
              pages={carouselData.length}
              selectedPage={activeIndex}
              pageIndicatorPosition="bottom"
            />
          </Flex>
        </Flex>
      )}
      {platform === 'mobile_web' && (
        <Flex flexDirection="column">
          <Flex justifyContent="center">
            <Flex
              flexDirection="column"
              background="primary"
              sx={{ boxShadow: '4px 8px 8px -6px rgba(0, 0, 0, 0.12)' }}
              borderRadius={radii.primary}
            >
              <StyledImage
                description={carouselData[activeIndex].description}
                height="auto"
                width="auto"
                src={carouselData[activeIndex].src}
              />
              <Box px="2x">
                <Flex mt="4x" ml="1x" alignItems="flex-start">
                  <Image description="quote" height="auto" width="auto" src={Quote} />
                </Flex>
                <Box paddingX="2x" pt="2x">
                  <Paragraph>{carouselData[activeIndex].comment}</Paragraph>
                  <Flex flexDirection="row" alignItems="center" flex="1" mt="4x">
                    <Box mr="1x">
                      <Trans>5.0</Trans>
                    </Box>
                    <Box>
                      <StarRating rating={5} />
                    </Box>
                  </Flex>
                </Box>
                <Flex justifyContent="flex-end" pr="4x" pb="4x">
                  <Image description="unquote" height="auto" width="auto" src={Unquote} />
                </Flex>
              </Box>
            </Flex>
          </Flex>
          <Flex justifyContent="center" mt="6x">
            <StyledButton icon={ArrowLeft} onClick={prevSlide} />
            <StyledButton icon={ArrowRight} onClick={nextSlide} />
          </Flex>
          <Flex position="relative" mt="12x" mr="2x">
            <CarouselPageIndicator
              pages={carouselData.length}
              selectedPage={activeIndex}
              pageIndicatorPosition="bottom"
            />
          </Flex>
        </Flex>
      )}
    </>
  );
};

export default Carousel;
